import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Download, Plus, Trash2, ShoppingCart } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import * as XLSX from 'xlsx';

// Probe type configurations
const PROBE_TYPES = {
  'FRET LC Probes': {
    scales: ['1 nmol', '2 nmol', '3 nmol', '5 nmol', '6 nmol', '10 nmol', '20 nmol', '30 nmol'],
    purification: 'HPLC',
    modifications: {
      five_prime: ['NA (inactive)'],
      three_prime: ['Phosphate'],
      internal: ['NA (inactive)']
    }
  },
  'FRET FL Probes': {
    scales: ['1 nmol', '2 nmol', '3 nmol', '5 nmol', '6 nmol', '10 nmol', '20 nmol', '30 nmol'],
    purification: 'HPLC',
    modifications: {
      five_prime: ['NA (inactive)'],
      three_prime: ['NA (inactive)'],
      internal: ['NA (inactive)']
    }
  },
  'SimpleMT Probe': {
    scales: ['1 nmol', '3 nmol', '5 nmol', '6 nmol', '10 nmol', '20 nmol', '30 nmol'],
    purification: 'HPLC',
    modifications: {
      five_prime: ['NA (inactive)'],
      three_prime: ['NA (inactive)'],
      internal: ['NA (inactive)']
    }
  },
  'Dual Label Hydrolysis Probe (Taqman)': {
    scales: ['5 nmol', '10 nmol', '20 nmol', '30 nmol', '40 nmol', '>50 nmol'],
    purification: 'HPLC',
    modifications: {
      five_prime: ['6-FAM', 'HEX', 'TET', 'JOE', 'Cy3', 'Cy5', 'ROX', 'TAMRA', 'Texas Red', 'VIC', 'NED', 'PET', 'LIZ'],
      three_prime: ['BHQ-1', 'BHQ-2', 'BBQ-650', 'DABCYL', 'ECLIPSE', 'TAMRA', 'Iowa Black FQ', 'Iowa Black RQ'],
      internal: ['DNA-Inosine', 'dU (Uracil)', 'LNA-A', 'LNA-C', 'LNA-G', 'LNA-T', 'MGB-Q530']
    }
  },
  'Dual Quenched Hydrolysis Probe (Taqman)': {
    scales: ['5 nmol', '10 nmol', '20 nmol', '30 nmol', '40 nmol', '>50 nmol'],
    purification: 'HPLC',
    modifications: {
      five_prime: ['6-FAM', 'HEX', 'TET', 'JOE', 'Cy3', 'Cy5', 'ROX', 'TAMRA', 'Texas Red', 'VIC', 'NED', 'PET', 'LIZ'],
      three_prime: ['BHQ-1', 'BHQ-2', 'BBQ-650', 'DABCYL', 'ECLIPSE', 'TAMRA', 'Iowa Black FQ', 'Iowa Black RQ'],
      internal: ['BHQ-2', 'ZEN', 'internal Quencher 530']
    }
  }
};

const PRIMER_SCALES = ['20 nmol', '40 nmol', '200 nmol', '1 µmol'];
const PRIMER_PURIFICATIONS = ['Desalted', 'HPLC', 'PAGE'];

const CustomProbesPrimers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, getItemCount } = useCart();
  const fileInputRef = useRef(null);

  const [oligos, setOligos] = useState([
    {
      id: Date.now(),
      name: '',
      type: 'Primer',
      sequence: '',
      probeType: '',
      scale: '20 nmol',
      purification: 'Desalted',
      fivePrimeMod: '',
      threePrimeMod: '',
      internalMod: ''
    }
  ]);

  // Validation function
  const validateOligoName = (name) => {
    if (!name || name.length === 0 || name.length > 16) return false;
    if (!/^[a-zA-Z0-9]/.test(name)) return false;
    const disallowed = /<|>|'|\/|\\|&|"|,|~/;
    return !disallowed.test(name);
  };

  const validateSequence = (seq) => {
    const allowed = /^[AGCTBDHKMNRSVWYagctbdhkmnrsvwy+]*$/;
    return allowed.test(seq);
  };

  const addOligo = () => {
    setOligos([...oligos, {
      id: Date.now(),
      name: '',
      type: 'Primer',
      sequence: '',
      probeType: '',
      scale: '20 nmol',
      purification: 'Desalted',
      fivePrimeMod: '',
      threePrimeMod: '',
      internalMod: ''
    }]);
  };

  const removeOligo = (id) => {
    if (oligos.length === 1) {
      toast.error('At least one oligo is required');
      return;
    }
    setOligos(oligos.filter(o => o.id !== id));
  };

  const updateOligo = (id, field, value) => {
    setOligos(oligos.map(o => {
      if (o.id === id) {
        const updated = { ...o, [field]: value };
        
        // Handle type change logic
        if (field === 'type') {
          if (value === 'Primer') {
            updated.probeType = '';
            updated.scale = '20 nmol';
            updated.purification = 'Desalted';
            updated.fivePrimeMod = '';
            updated.threePrimeMod = '';
            updated.internalMod = '';
          } else {
            updated.scale = '5 nmol';
            updated.purification = 'HPLC';
          }
        }
        
        // Handle probe type change
        if (field === 'probeType' && value) {
          const config = PROBE_TYPES[value];
          if (config) {
            updated.scale = config.scales[0];
            updated.purification = config.purification;
            updated.fivePrimeMod = '';
            updated.threePrimeMod = '';
            updated.internalMod = '';
          }
        }
        
        return updated;
      }
      return o;
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const importedOligos = jsonData.map((row, index) => ({
        id: Date.now() + index,
        name: row['DNA Oligo Name'] || '',
        type: row['Oligo Type'] || 'Primer',
        sequence: row['DNA Sequence (5\' -> 3\')'] || '',
        probeType: row['Probe Type'] || '',
        scale: row['DNA Scale'] || '20 nmol',
        purification: row['DNA Purification'] || 'Desalted',
        fivePrimeMod: row['5\' Modification'] || '',
        threePrimeMod: row['3\' Modification'] || '',
        internalMod: row['Inner Modification'] || ''
      }));

      if (importedOligos.length > 0) {
        setOligos(importedOligos);
        toast.success(`Imported ${importedOligos.length} oligo(s)`);
      } else {
        toast.error('No valid data found in file');
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error('Failed to parse Excel file');
    }
  };

  const calculatePrice = (oligo) => {
    // Base pricing logic
    const basePrice = oligo.type === 'Primer' ? 15 : 200;
    const lengthMultiplier = Math.ceil(oligo.sequence.length / 10);
    let price = basePrice * lengthMultiplier;

    // Add modification costs
    if (oligo.fivePrimeMod && oligo.fivePrimeMod !== 'NA (inactive)') price += 50;
    if (oligo.threePrimeMod && oligo.threePrimeMod !== 'NA (inactive)') price += 50;
    if (oligo.internalMod && oligo.internalMod !== 'NA (inactive)') price += 75;

    return price;
  };

  const handleAddToCart = async () => {
    // Validate all oligos
    const errors = [];
    oligos.forEach((oligo, index) => {
      if (!validateOligoName(oligo.name)) {
        errors.push(`Row ${index + 1}: Invalid oligo name`);
      }
      if (!oligo.sequence || !validateSequence(oligo.sequence)) {
        errors.push(`Row ${index + 1}: Invalid sequence`);
      }
      if (oligo.type === 'Probe' && !oligo.probeType) {
        errors.push(`Row ${index + 1}: Probe type required`);
      }
    });

    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    try {
      let totalPrice = 0;
      for (const oligo of oligos) {
        const price = calculatePrice(oligo);
        totalPrice += price;
      }

      await addToCart({
        technology: 'custom_oligos',
        application: 'custom_probes_primers',
        configuration: {
          oligos: oligos.map(o => ({
            name: o.name,
            type: o.type,
            sequence: o.sequence,
            probeType: o.probeType,
            scale: o.scale,
            purification: o.purification,
            modifications: {
              five_prime: o.fivePrimeMod,
              three_prime: o.threePrimeMod,
              internal: o.internalMod
            }
          })),
          total_oligos: oligos.length
        },
        price: totalPrice,
        quantity: 1
      });

      toast.success(`Added ${oligos.length} oligo(s) to cart (€${totalPrice.toFixed(2)})`);
    } catch (error) {
      toast.error('Failed to add to cart');
      console.error(error);
    }
  };

  const getTotalPrice = () => {
    return oligos.reduce((sum, oligo) => sum + calculatePrice(oligo), 0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-gray-900">Custom Order Your Probes & Primers</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/workflow')}
                data-testid="change-application-button"
              >
                Change Application
              </Button>
              {user && (
                <Button onClick={() => navigate('/cart')} variant="outline" className="relative">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Cart
                  {getItemCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getItemCount()}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Title and Description */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Custom Order Your Probes & Primers</h1>
            <p className="text-gray-600">
              Below, you can add and order all your own primers and probes individually or in bulk.
            </p>
          </div>

          {/* Bulk Upload Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Bulk Upload</h2>
            <div className="flex flex-wrap gap-4">
              <a
                href="/Upload-Form-Custom.xlsx"
                download
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Download primer-probe order template
              </a>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload your primers and probes
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Manual Entry Table */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Enter Here</h2>
              <Button onClick={addOligo} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Row
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">#</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Oligo Name*</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Oligo Type*</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Sequence*</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Probe Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Scale*</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Purification*</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">5' Mod</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">3' Mod</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Internal Mod</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Price</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {oligos.map((oligo, index) => (
                    <tr key={oligo.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                      
                      {/* Oligo Name */}
                      <td className="px-4 py-3">
                        <Input
                          value={oligo.name}
                          onChange={(e) => updateOligo(oligo.id, 'name', e.target.value)}
                          placeholder="Name"
                          className={`w-32 ${!validateOligoName(oligo.name) && oligo.name ? 'border-red-500' : ''}`}
                        />
                      </td>

                      {/* Oligo Type */}
                      <td className="px-4 py-3">
                        <Select value={oligo.type} onValueChange={(val) => updateOligo(oligo.id, 'type', val)}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Primer">Primer</SelectItem>
                            <SelectItem value="Probe">Probe</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Sequence */}
                      <td className="px-4 py-3">
                        <Textarea
                          value={oligo.sequence}
                          onChange={(e) => updateOligo(oligo.id, 'sequence', e.target.value.toUpperCase())}
                          placeholder="ATGC..."
                          rows={2}
                          className={`w-48 font-mono text-sm ${!validateSequence(oligo.sequence) && oligo.sequence ? 'border-red-500' : ''}`}
                        />
                      </td>

                      {/* Probe Type */}
                      <td className="px-4 py-3">
                        <Select 
                          value={oligo.probeType} 
                          onValueChange={(val) => updateOligo(oligo.id, 'probeType', val)}
                          disabled={oligo.type === 'Primer'}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(PROBE_TYPES).map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Scale */}
                      <td className="px-4 py-3">
                        <Select 
                          value={oligo.scale} 
                          onValueChange={(val) => updateOligo(oligo.id, 'scale', val)}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(oligo.type === 'Primer' 
                              ? PRIMER_SCALES 
                              : (oligo.probeType && PROBE_TYPES[oligo.probeType]?.scales) || ['5 nmol']
                            ).map(scale => (
                              <SelectItem key={scale} value={scale}>{scale}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Purification */}
                      <td className="px-4 py-3">
                        {oligo.type === 'Probe' ? (
                          <span className="text-sm text-gray-600">HPLC</span>
                        ) : (
                          <Select 
                            value={oligo.purification} 
                            onValueChange={(val) => updateOligo(oligo.id, 'purification', val)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PRIMER_PURIFICATIONS.map(pur => (
                                <SelectItem key={pur} value={pur}>{pur}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </td>

                      {/* 5' Modification */}
                      <td className="px-4 py-3">
                        <Select 
                          value={oligo.fivePrimeMod || 'none'} 
                          onValueChange={(val) => updateOligo(oligo.id, 'fivePrimeMod', val === 'none' ? '' : val)}
                          disabled={oligo.type === 'Primer'}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {oligo.probeType && PROBE_TYPES[oligo.probeType]?.modifications.five_prime.map(mod => (
                              <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* 3' Modification */}
                      <td className="px-4 py-3">
                        <Select 
                          value={oligo.threePrimeMod || 'none'} 
                          onValueChange={(val) => updateOligo(oligo.id, 'threePrimeMod', val === 'none' ? '' : val)}
                          disabled={oligo.type === 'Primer'}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {oligo.probeType && PROBE_TYPES[oligo.probeType]?.modifications.three_prime.map(mod => (
                              <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Internal Modification */}
                      <td className="px-4 py-3">
                        <Select 
                          value={oligo.internalMod || 'none'} 
                          onValueChange={(val) => updateOligo(oligo.id, 'internalMod', val === 'none' ? '' : val)}
                          disabled={oligo.type === 'Primer'}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {oligo.probeType && PROBE_TYPES[oligo.probeType]?.modifications.internal.map(mod => (
                              <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Price */}
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        €{calculatePrice(oligo).toFixed(2)}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeOligo(oligo.id)}
                          disabled={oligos.length === 1}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary and Actions */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Total Oligos: {oligos.length}</p>
                <p className="text-2xl font-bold text-gray-900">Total: €{getTotalPrice().toFixed(2)}</p>
              </div>
              <Button 
                onClick={handleAddToCart}
                className="bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomProbesPrimers;
